import type { OptimizerQueryState } from "../query-state";

export type FilterSheetSection = "world" | "extra";

export type SheetWorldSlice = Pick<
  OptimizerQueryState,
  "marketScope" | "gameDays" | "startsWithin" | "live" | "slipType"
>;

export type WorldQuerySlice = SheetWorldSlice &
  Pick<OptimizerQueryState, "sportsbook" | "platform" | "leagues">;

export type ExtraQuerySlice = Pick<
  OptimizerQueryState,
  | "statTypes"
  | "matchups"
  | "minEV"
  | "minDataPoints"
  | "minLiquidity"
  | "minOddsToHit"
  | "maxOddsToHit"
  | "minOdds"
  | "maxOdds"
  | "requiredComparisonBooks"
  | "weights"
  | "preset"
  | "requiredBooks"
  | "hidePushProps"
  | "overUnder"
>;

export type CompactSheetSlice = Pick<
  OptimizerQueryState,
  "playerSearch" | "sort" | "order"
>;

export const CLEARED_EXTRA_QUERY: ExtraQuerySlice = {
  statTypes: [],
  matchups: [],
  minEV: 0,
  minDataPoints: 0,
  minLiquidity: 0,
  minOddsToHit: 0,
  maxOddsToHit: 100,
  minOdds: null,
  maxOdds: null,
  requiredComparisonBooks: [],
  weights: null,
  preset: null,
  requiredBooks: [],
  hidePushProps: false,
  overUnder: "",
};

const CLEARED_SHEET_WORLD: SheetWorldSlice = {
  marketScope: "",
  gameDays: [],
  startsWithin: 0,
  live: false,
  slipType: "",
};

export type FilterSheetDrafts = {
  section: FilterSheetSection;
  extra: ExtraQuerySlice;
  compact: CompactSheetSlice;
};

function cloneStringArray<T extends string>(values: readonly T[]): T[] {
  return [...values];
}

function cloneWeights(
  weights: ExtraQuerySlice["weights"],
): ExtraQuerySlice["weights"] {
  return weights == null ? null : { ...weights };
}

export function pickCompactSheet(state: OptimizerQueryState): CompactSheetSlice {
  return {
    playerSearch: state.playerSearch,
    sort: state.sort,
    order: state.order,
  };
}

export function pickExtraQuery(state: OptimizerQueryState): ExtraQuerySlice {
  return {
    statTypes: cloneStringArray(state.statTypes),
    matchups: cloneStringArray(state.matchups),
    minEV: state.minEV,
    minDataPoints: state.minDataPoints,
    minLiquidity: state.minLiquidity,
    minOddsToHit: state.minOddsToHit,
    maxOddsToHit: state.maxOddsToHit,
    minOdds: state.minOdds,
    maxOdds: state.maxOdds,
    requiredComparisonBooks: cloneStringArray(state.requiredComparisonBooks),
    weights: cloneWeights(state.weights),
    preset: state.preset,
    requiredBooks: cloneStringArray(state.requiredBooks),
    hidePushProps: state.hidePushProps,
    overUnder: state.overUnder,
  };
}

export function applyCompactSheet(
  state: OptimizerQueryState,
  compact: CompactSheetSlice,
): OptimizerQueryState {
  return {
    ...state,
    playerSearch: compact.playerSearch,
    sort: compact.sort,
    order: compact.order,
  };
}

export function applyExtraQuery(
  state: OptimizerQueryState,
  extra: ExtraQuerySlice,
): OptimizerQueryState {
  return {
    ...state,
    statTypes: cloneStringArray(extra.statTypes),
    matchups: cloneStringArray(extra.matchups),
    minEV: extra.minEV,
    minDataPoints: extra.minDataPoints,
    minLiquidity: extra.minLiquidity,
    minOddsToHit: extra.minOddsToHit,
    maxOddsToHit: extra.maxOddsToHit,
    minOdds: extra.minOdds,
    maxOdds: extra.maxOdds,
    requiredComparisonBooks: cloneStringArray(extra.requiredComparisonBooks),
    weights: cloneWeights(extra.weights),
    preset: extra.preset,
    requiredBooks: cloneStringArray(extra.requiredBooks),
    hidePushProps: extra.hidePushProps,
    overUnder: extra.overUnder,
  };
}

function stringArraysEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function weightsEqual(
  a: ExtraQuerySlice["weights"],
  b: ExtraQuerySlice["weights"],
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key, index) => key === bKeys[index] && a[key] === b[key]);
}

const EXTRA_QUERY_KEYS = Object.keys(
  CLEARED_EXTRA_QUERY,
) as (keyof ExtraQuerySlice)[];

const WORLD_QUERY_KEY_TABLE: Record<keyof WorldQuerySlice, true> = {
  sportsbook: true,
  platform: true,
  leagues: true,
  marketScope: true,
  gameDays: true,
  startsWithin: true,
  live: true,
  slipType: true,
};
const WORLD_QUERY_KEYS = Object.keys(
  WORLD_QUERY_KEY_TABLE,
) as (keyof WorldQuerySlice)[];

const COMPACT_SHEET_KEY_TABLE: Record<keyof CompactSheetSlice, true> = {
  playerSearch: true,
  sort: true,
  order: true,
};
const COMPACT_SHEET_KEYS = Object.keys(
  COMPACT_SHEET_KEY_TABLE,
) as (keyof CompactSheetSlice)[];

function extraKeyEquals(
  key: keyof ExtraQuerySlice,
  a: ExtraQuerySlice,
  b: ExtraQuerySlice,
): boolean {
  switch (key) {
    case "statTypes":
      return stringArraysEqual(a.statTypes, b.statTypes);
    case "matchups":
      return stringArraysEqual(a.matchups, b.matchups);
    case "requiredBooks":
      return stringArraysEqual(a.requiredBooks, b.requiredBooks);
    case "requiredComparisonBooks":
      return stringArraysEqual([...a.requiredComparisonBooks].sort(), [...b.requiredComparisonBooks].sort());
    case "weights":
      return weightsEqual(a.weights, b.weights);
    default:
      return a[key] === b[key];
  }
}

export function extraQueryEquals(a: ExtraQuerySlice, b: ExtraQuerySlice): boolean {
  return EXTRA_QUERY_KEYS.every((key) => extraKeyEquals(key, a, b));
}

export function diffExtraQuery(
  next: ExtraQuerySlice,
  current: ExtraQuerySlice,
): Partial<ExtraQuerySlice> {
  const changed: Partial<ExtraQuerySlice> = {};
  for (const key of EXTRA_QUERY_KEYS) {
    if (!extraKeyEquals(key, next, current)) {
      (changed as Record<string, unknown>)[key] = next[key];
    }
  }
  return changed;
}

export function openFilterSheet(
  section: FilterSheetSection,
  state: OptimizerQueryState,
): FilterSheetDrafts {
  return {
    section,
    extra: pickExtraQuery(state),
    compact: pickCompactSheet(state),
  };
}

export function patchSheetExtra(
  drafts: FilterSheetDrafts | null,
  extra: Partial<ExtraQuerySlice>,
): FilterSheetDrafts | null {
  if (!drafts) return drafts;
  return { ...drafts, extra: { ...drafts.extra, ...extra } };
}

export function commitFilterSheet(
  drafts: FilterSheetDrafts | null,
  committed: OptimizerQueryState,
): OptimizerQueryState {
  if (!drafts) return committed;
  return applyCompactSheet(applyExtraQuery(committed, drafts.extra), drafts.compact);
}

function pickDefinedKeys<Slice>(
  update: Partial<OptimizerQueryState>,
  keys: readonly (keyof Slice & keyof OptimizerQueryState)[],
): Partial<Slice> {
  const picked: Partial<Slice> = {};
  for (const key of keys) {
    const value = update[key];
    if (value !== undefined) (picked as Record<string, unknown>)[key] = value;
  }
  return picked;
}

export function splitSheetUpdate(update: Partial<OptimizerQueryState>): {
  world: Partial<WorldQuerySlice>;
  extra: Partial<ExtraQuerySlice>;
  compact: Partial<CompactSheetSlice>;
} {
  return {
    world: pickDefinedKeys<WorldQuerySlice>(update, WORLD_QUERY_KEYS),
    extra: pickDefinedKeys<ExtraQuerySlice>(update, EXTRA_QUERY_KEYS),
    compact: pickDefinedKeys<CompactSheetSlice>(update, COMPACT_SHEET_KEYS),
  };
}

export function clearedSheetUpdate(
  state: OptimizerQueryState,
): Partial<OptimizerQueryState> {
  const world: Partial<SheetWorldSlice> = {};
  for (const [key, value] of Object.entries(CLEARED_SHEET_WORLD)) {
    if (JSON.stringify(state[key as keyof SheetWorldSlice]) !== JSON.stringify(value)) {
      (world as Record<string, unknown>)[key] = value;
    }
  }
  return { ...world, ...CLEARED_EXTRA_QUERY };
}

export function hasSliceKeys(slice: object): boolean {
  return Object.keys(slice).length > 0;
}

export function sheetStateForForm(
  drafts: FilterSheetDrafts | null,
  committed: OptimizerQueryState,
): OptimizerQueryState {
  return commitFilterSheet(drafts, committed);
}
